import { toPbDateTime } from "@/lib/dates";
import { pb, PB_URL } from "@/lib/pb";
import type { InboxItemsResponse, InboxSettingsResponse } from "@/lib/pb-types";
import { sortByReceived, toInboxItem, type InboxItem, type InboxStatus } from "./model";

/**
 * Every read and write the inbox makes against PocketBase.
 *
 * The drop-off itself is not here and cannot be: it is `POST /api/inbox/submit`
 * in `pb/pb_hooks/inbox.pb.js`, authenticated with a shared token rather than
 * with a session, and called by people who do not have this app open. This file
 * is only the owner's side of that — reading what arrived, marking it, and
 * managing the token those callers use.
 */

type RequestScope = { signal?: AbortSignal };

/* -------------------------------------------------------------------------- */
/* Items                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Everything in the inbox, newest first.
 *
 * The whole collection rather than a page: an inbox that has to be paginated is
 * an inbox that has stopped being triaged, and the filter chips need the counts
 * for the states they are not currently showing. Archiving is what keeps this
 * list from growing without bound.
 */
export async function fetchInboxItems(scope: RequestScope = {}): Promise<InboxItem[]> {
  const records = await pb.collection("inbox_items").getFullList({
    sort: "-created",
    signal: scope.signal,
    requestKey: null,
  });
  return sortByReceived(records.map(toInboxItem));
}

/**
 * Move an item through the state machine.
 *
 * `read_at` is stamped the **first** time an item is opened and never rewritten:
 * "when did I first see this" is the useful fact, and re-reading something on
 * Friday should not make it look as though Tuesday's request went unnoticed.
 * Nothing clears it when an item goes back to `new` either — that is an undo of
 * the triage, not of history.
 */
export async function setInboxStatus(
  item: InboxItem,
  status: InboxStatus,
): Promise<InboxItem> {
  const body: Record<string, unknown> = { status };
  if (status !== "new" && !item.readAt) {
    body.read_at = toPbDateTime();
  }
  const record = (await pb.collection("inbox_items").update(item.id, body)) as InboxItemsResponse;
  return toInboxItem(record);
}

/*
 * There is deliberately no `createInboxItem` here.
 *
 * Everything in this collection arrives from outside — that is what the inbox
 * *is*. A create button in the app would be a way of writing a note to yourself,
 * which is what the tasks screen already does better, and it would put rows in
 * the list that claim to have a sender when nobody sent them. The collection's
 * `createRule` still admits the signed-in user, so this stays a one-line
 * addition if a second channel ever needs it; `source` is the field that would
 * tell the two apart.
 */

export async function deleteInboxItem(id: string): Promise<void> {
  await pb.collection("inbox_items").delete(id);
}

/* -------------------------------------------------------------------------- */
/* The shared token                                                            */
/* -------------------------------------------------------------------------- */

export type InboxSettings = {
  id: string;
  token: string;
};

/**
 * The single settings record.
 *
 * Created by the migration, never by the app: `createRule` is superuser-only
 * precisely so a second one cannot appear, because the hook resolves "the" token
 * by taking the first record and two of them would make which token works depend
 * on insertion order. A missing record therefore means the migration has not run
 * on this server, which is worth saying out loud rather than papering over.
 */
export async function fetchInboxSettings(scope: RequestScope = {}): Promise<InboxSettings> {
  const record = (await pb
    .collection("inbox_settings")
    .getFirstListItem("", { signal: scope.signal, requestKey: null })) as InboxSettingsResponse;
  return { id: record.id, token: record.token };
}

/** The alphabet the seeded token uses: no case to get wrong when read aloud. */
const TOKEN_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";
const TOKEN_LENGTH = 40;

/**
 * A fresh token, generated in the browser.
 *
 * `crypto.getRandomValues` and not `Math.random`, obviously — but the modulo is
 * worth a word too: 256 is not a multiple of 36, so a naive `byte % 36` would
 * make the first four letters of the alphabet slightly likelier than the rest.
 * Bytes landing in the biased tail are discarded and redrawn instead.
 */
export function generateInboxToken(length: number = TOKEN_LENGTH): string {
  const limit = Math.floor(256 / TOKEN_ALPHABET.length) * TOKEN_ALPHABET.length;
  let out = "";
  const buffer = new Uint8Array(length);
  while (out.length < length) {
    crypto.getRandomValues(buffer);
    for (const byte of buffer) {
      if (byte >= limit) {
        continue;
      }
      out += TOKEN_ALPHABET[byte % TOKEN_ALPHABET.length];
      if (out.length === length) {
        break;
      }
    }
  }
  return out;
}

/**
 * Rotate the token.
 *
 * Immediate and total: the hook reads the record on every request, so the
 * previous token stops working on the next call anybody makes with it. That is
 * the point — this is the revoke button — and it is why the UI asks first.
 */
export async function rotateInboxToken(id: string): Promise<InboxSettings> {
  return setInboxToken(id, generateInboxToken());
}

/** The schema's own limits, so the field can explain a rejection before sending it. */
export const TOKEN_MIN_LENGTH = 24;
export const TOKEN_MAX_LENGTH = 100;

/**
 * Why a token cannot be used, or null when it can.
 *
 * The length bounds mirror `pb_migrations/*_create_inbox.js` exactly — the
 * server is the authority and will reject a bad one, but a rejection that only
 * arrives after a round trip is a rejection the field could have explained.
 *
 * Whitespace is refused for a reason the schema does not know about: the value
 * is carried in an HTTP header, where a space ends the token and a newline ends
 * the header. A token with a stray space pasted into it would be silently
 * truncated by the transport rather than rejected, which is the worst of the
 * available failures.
 */
export function tokenRejection(value: string): string | null {
  if (value.length < TOKEN_MIN_LENGTH) {
    return `Too short — at least ${TOKEN_MIN_LENGTH} characters, so it cannot be guessed.`;
  }
  if (value.length > TOKEN_MAX_LENGTH) {
    return `Too long — at most ${TOKEN_MAX_LENGTH} characters.`;
  }
  if (/\s/.test(value)) {
    return "No spaces or line breaks: the token is sent in an HTTP header, which would cut it short.";
  }
  return null;
}

/**
 * Set the token to a specific value.
 *
 * Used both by {@link rotateInboxToken} and by the "set your own" field, so
 * there is exactly one write path and the same validation guards both.
 */
export async function setInboxToken(id: string, token: string): Promise<InboxSettings> {
  const value = token.trim();
  const rejection = tokenRejection(value);
  if (rejection) {
    throw new Error(rejection);
  }
  const record = (await pb
    .collection("inbox_settings")
    .update(id, { token: value })) as InboxSettingsResponse;
  return { id: record.id, token: record.token };
}

/* -------------------------------------------------------------------------- */
/* What to hand a colleague                                                    */
/* -------------------------------------------------------------------------- */

/** The URL the endpoint answers on, for this build's server. */
export const INBOX_SUBMIT_URL = `${PB_URL.replace(/\/+$/, "")}/api/inbox/submit`;

/**
 * A ready-to-run `curl`, with the real token in it.
 *
 * Copyable as one blob because that is how it will actually be sent — pasted
 * into a chat window for a colleague, or into an AI agent's tool config. A
 * snippet with `<YOUR_TOKEN>` in it is a snippet that gets sent unedited and
 * then reported as broken.
 */
export function curlExample(token: string): string {
  return [
    `curl -X POST ${INBOX_SUBMIT_URL} \\`,
    `  -H "Authorization: Bearer ${token}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '{"sender":"Your name","title":"What needs doing","description":"Any detail"}'`,
  ].join("\n");
}

/**
 * The schema limits the hook enforces, mirrored from `pb/pb_hooks/inbox-lib.js`.
 *
 * Duplicated for the same reason {@link TOKEN_MIN_LENGTH} is: the server is the
 * authority, but a briefing that omits the limits is a briefing whose reader
 * discovers them by being rejected.
 */
const FIELD_LIMITS = {
  sender: 120,
  title: 500,
  description: 20_000,
  source: 60,
  attachments: 10,
  attachmentBytes: "5 MB each, 10 MB per request",
  // Mirrors the allowlist on `inbox_items.attachments`. Worth stating because
  // the server types an upload by sniffing its content, so a caller cannot get
  // an unsupported file through by renaming it — and an agent that learns this
  // from a 400 has already wasted the round trip.
  attachmentTypes: "PNG, JPEG, WebP, GIF, HEIC, PDF, plain text, Markdown, CSV, HTML",
} as const;

/**
 * One block of text that teaches an AI agent the whole endpoint.
 *
 * The two `curl` snippets above are for a human who will run them; this is for
 * an agent that has to *call* the endpoint from its own code, which needs
 * different facts — every field with its limit, what the server stamps rather
 * than accepts, the exact success and failure shapes, and the health check to
 * try first. It is one textarea and one Copy button because the way this
 * actually gets used is: select all, paste into a chat with an agent, done. Any
 * form that requires assembling two copies into one message is a form where the
 * token arrives without the URL.
 *
 * The real token is interpolated, like the curl snippets and for the same
 * reason — a briefing containing `<YOUR_TOKEN>` is a briefing that gets pasted
 * unedited and then reported as broken.
 */
export function agentBriefing(token: string): string {
  return `You can send work to my Kobo inbox. Everything you need is below — the credentials are real, nothing needs filling in.

ENDPOINT
  POST ${INBOX_SUBMIT_URL}
  Header: Authorization: Bearer ${token}
  (X-Inbox-Token: ${token} works too, if a Bearer header is awkward.)

HEALTH CHECK (do this first to confirm the token works)
  GET ${PB_URL.replace(/\/+$/, "")}/api/inbox/ping
  Same Authorization header. It creates nothing.

BODY — JSON, form-urlencoded or multipart, all accepted
  sender       required, max ${FIELD_LIMITS.sender} chars — who is dropping it off; put your own name here
  title        required, max ${FIELD_LIMITS.title} chars — what needs doing, in one line
  description  optional, max ${FIELD_LIMITS.description} chars — the detail, context, links
  source       optional, max ${FIELD_LIMITS.source} chars — the channel you are calling from; defaults to "api"

  Attachments require multipart: field name "attachments", up to ${FIELD_LIMITS.attachments} files, ${FIELD_LIMITS.attachmentBytes}.
  Accepted file types: ${FIELD_LIMITS.attachmentTypes}. The server checks the file's actual content, so renaming an unsupported one will not get it through.
  Any other field you send is ignored. Status and receipt time are stamped by the server, so you cannot post something pre-read or backdated.

EXAMPLE
  curl -X POST ${INBOX_SUBMIT_URL} \\
    -H "Authorization: Bearer ${token}" \\
    -H "Content-Type: application/json" \\
    -d '{"sender":"Your name","title":"What needs doing","description":"Any detail"}'

RESPONSES
  200  {"ok":true,"id":"...","title":"...","sender":"...","created":"...","attachments":[...]}
  400  the body could not be read, or a required field is missing or too long — the message says which
  401  the token is wrong or missing

NOTES
  Every accepted call becomes one new item in my inbox for me to triage, so send one item per request rather than batching several requests into one title.`;
}

/** The same call with a file attached, which has to be multipart rather than JSON. */
export function curlWithAttachmentExample(token: string): string {
  return [
    `curl -X POST ${INBOX_SUBMIT_URL} \\`,
    `  -H "Authorization: Bearer ${token}" \\`,
    `  -F "sender=Your name" \\`,
    `  -F "title=What needs doing" \\`,
    `  -F "description=Any detail" \\`,
    `  -F "attachments=@/path/to/screenshot.png"`,
  ].join("\n");
}
